import { useState } from "react";
import type { Folder } from "../api/types";
import {
  Check,
  ChevronDown,
  FolderClosed,
  LibraryBig,
  PenLine,
  Plus,
  X,
} from "../compose/Icon";

/**
 * Folder navigation — composes the ARIA-tree semantics of `@norkron/web-tree`.
 * "All documents" plus one row per folder; the active folder filters the doc
 * list. The tree doubles as a status board (design screens §8).
 *
 * When `canManage` (workspace staff), the tree also carries the folder-management
 * affordances: a "New folder" row and an inline rename per folder. Both are
 * optimistic-free — they call the injected `onCreate` / `onRename` (which own the
 * fenced `/api` write + toast + reload) and surface a typed error inline on
 * failure (409 duplicate name, 403, …). Selection + status filtering are
 * untouched, so the existing tree keeps working for every reader.
 */
export function FolderNav({
  folders,
  active,
  onSelect,
  total,
  canManage = false,
  onCreate,
  onRename,
}: {
  folders: Folder[];
  active: string | null;
  onSelect: (folder: string | null) => void;
  /** The workspace-wide document total shown on the "All documents" node. It
   *  includes FOLDERLESS/root documents, which `folders[].doc_count` omits — so
   *  summing the folders undercounts (the sidebar-vs-header mismatch). When a
   *  caller cannot supply it (e.g. a test), it falls back to the folder sum. */
  total?: number;
  canManage?: boolean;
  onCreate?: (name: string) => Promise<void>;
  onRename?: (id: string, name: string) => Promise<void>;
}) {
  const allDocuments = total ?? folders.reduce((n, f) => n + f.doc_count, 0);
  const manage = canManage && (onCreate != null || onRename != null);

  const [creating, setCreating] = useState(false);
  const [createName, setCreateName] = useState("");
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function resetCreate() {
    setCreating(false);
    setCreateName("");
    setError(null);
  }
  function resetRename() {
    setRenamingId(null);
    setDraft("");
    setError(null);
  }
  function beginRename(f: Folder) {
    setRenamingId(f.id);
    setDraft(f.name);
    setCreating(false);
    setError(null);
  }

  async function submitCreate(e: React.FormEvent) {
    e.preventDefault();
    const name = createName.trim();
    if (!name || !onCreate) return;
    setBusy(true);
    setError(null);
    try {
      await onCreate(name);
      resetCreate();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create the folder.");
    } finally {
      setBusy(false);
    }
  }

  async function submitRename(e: React.FormEvent, id: string) {
    e.preventDefault();
    const name = draft.trim();
    if (!name || !onRename) return;
    setBusy(true);
    setError(null);
    try {
      await onRename(id, name);
      resetRename();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not rename the folder.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <ul className="tree" role="tree" aria-label="Folders">
        <li role="none">
          <button
            type="button"
            role="treeitem"
            aria-selected={active === null}
            className={`tree__row${active === null ? " tree__row--active" : ""}`}
            onClick={() => onSelect(null)}
            data-testid="folder-all"
          >
            <ChevronDown className="tree__caret tree__caret--open" size={14} strokeWidth={1.5} />
            <LibraryBig size={15} strokeWidth={1.5} />
            <span>All documents</span>
            <span className="tree__count">{allDocuments}</span>
          </button>
        </li>
        <li role="none">
          <ul className="tree__group" role="group">
            {folders.map((f) =>
              renamingId === f.id ? (
                <li role="none" key={f.id}>
                  <form
                    className="tree__row"
                    style={{ cursor: "default" }}
                    onSubmit={(e) => submitRename(e, f.id)}
                  >
                    <FolderClosed size={15} strokeWidth={1.5} />
                    <input
                      className="field__input"
                      style={{ flex: 1, minWidth: 0, padding: "4px 6px" }}
                      value={draft}
                      autoFocus
                      disabled={busy}
                      aria-label={`Rename folder ${f.name}`}
                      data-testid={`folder-rename-input-${f.id}`}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") resetRename();
                      }}
                    />
                    <IconButton
                      type="submit"
                      label="Save folder name"
                      disabled={busy}
                      testid={`folder-rename-save-${f.id}`}
                    >
                      <Check size={15} strokeWidth={1.5} />
                    </IconButton>
                    <IconButton
                      type="button"
                      label="Cancel rename"
                      disabled={busy}
                      testid={`folder-rename-cancel-${f.id}`}
                      onClick={resetRename}
                    >
                      <X size={15} strokeWidth={1.5} />
                    </IconButton>
                  </form>
                </li>
              ) : (
                <li role="none" key={f.id}>
                  <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <button
                      type="button"
                      role="treeitem"
                      aria-selected={active === f.name}
                      className={`tree__row${active === f.name ? " tree__row--active" : ""}`}
                      style={{ flex: 1, width: "auto", minWidth: 0 }}
                      onClick={() => onSelect(f.name)}
                      data-testid={`folder-${f.id}`}
                    >
                      <FolderClosed size={15} strokeWidth={1.5} />
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {f.name}
                      </span>
                      <span className="tree__count">{f.doc_count}</span>
                    </button>
                    {manage && onRename ? (
                      <IconButton
                        type="button"
                        label={`Rename ${f.name}`}
                        testid={`folder-rename-${f.id}`}
                        onClick={() => beginRename(f)}
                      >
                        <PenLine size={14} strokeWidth={1.5} />
                      </IconButton>
                    ) : null}
                  </div>
                </li>
              ),
            )}

            {manage && onCreate ? (
              <li role="none">
                {creating ? (
                  <form
                    className="tree__row"
                    style={{ cursor: "default" }}
                    onSubmit={submitCreate}
                  >
                    <Plus size={15} strokeWidth={1.5} />
                    <input
                      className="field__input"
                      style={{ flex: 1, minWidth: 0, padding: "4px 6px" }}
                      value={createName}
                      autoFocus
                      disabled={busy}
                      placeholder="Folder name"
                      aria-label="New folder name"
                      data-testid="folder-new-input"
                      onChange={(e) => setCreateName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") resetCreate();
                      }}
                    />
                    <IconButton
                      type="submit"
                      label="Create folder"
                      disabled={busy}
                      testid="folder-new-save"
                    >
                      <Check size={15} strokeWidth={1.5} />
                    </IconButton>
                    <IconButton
                      type="button"
                      label="Cancel new folder"
                      disabled={busy}
                      testid="folder-new-cancel"
                      onClick={resetCreate}
                    >
                      <X size={15} strokeWidth={1.5} />
                    </IconButton>
                  </form>
                ) : (
                  <button
                    type="button"
                    className="tree__row"
                    onClick={() => {
                      resetRename();
                      setCreating(true);
                    }}
                    data-testid="folder-new"
                  >
                    <Plus size={15} strokeWidth={1.5} />
                    <span>New folder</span>
                  </button>
                )}
              </li>
            ) : null}
          </ul>
        </li>
      </ul>
      {error ? (
        <div className="field__error" role="alert" data-testid="folder-error" style={{ marginTop: 6 }}>
          {error}
        </div>
      ) : null}
    </>
  );
}

/** A compact icon-only button for the tree affordances (token-styled ghost). */
function IconButton({
  children,
  label,
  testid,
  type,
  disabled,
  onClick,
}: {
  children: React.ReactNode;
  label: string;
  testid: string;
  type: "button" | "submit";
  disabled?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type={type}
      className="btn btn--ghost"
      style={{ padding: 5, flex: "none" }}
      aria-label={label}
      title={label}
      disabled={disabled}
      data-testid={testid}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
